
















































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { BarChart, ExtractComponentData } from "vue-chart-3";
import MiraFactsComponent from "@/components/mira/MiraFacts.vue";
import { MiraTrendSeries } from "@/data/models/Mira";
import { COLOR_MIRA_TREND_PERIOD, COLOR_MIRA_TREND_PERIOD_DARK, COLOR_STANDARD_WHITE, COLOR_SURFACE_DARK } from "@/utils/colors";
import { MiraViewContext, formatPercent } from "@/utils/MiraDataQualityView";
import { casesText } from "@/utils/MiraCaseUnits";
import { MiraFact } from "@/utils/MiraDryingView";
import { MIRA_TREND_ANIMATION, MiraValueFormatter, chartAxisKey, trendPeriodAnnotation, trendValueAxis } from "@/utils/MiraTrendChart";
import { MiraTrendGrainModule } from "@/store/modules/MiraTrendGrainModule";
import {
  MIRA_COMPARISON_GRAINS,
  MIRA_TREND_GRAINS,
  MiraTrendGrain,
  MiraTrendRange,
  grainForRange,
  isTotalMetric,
  offeredGrain,
} from "@/utils/MiraTrendView";
import {
  MIRA_COMPARISON_BAR_FLOOR,
  MiraStackEntity,
  MiraStackedSegmentDef,
  MiraStackedTrendView,
  buildAverageComparisonBars,
  buildComparisonBars,
  buildStackedTrend,
} from "@/utils/MiraStackedTrendView";
import { themed } from "@/utils/Theme";

/** The alpha a whole bar takes when the period is partial (the year to date). */
const PARTIAL_ALPHA = "73";
/** The alpha a part not under the pointer fades to, so hovering one segment dims the others (Firebase style). */
const DIM_ALPHA = "40";

/** Appends an alpha byte to a `#rrggbb`; leaves an already-aliased colour untouched. */
function withAlpha(color: string, alpha: string): string {
  return color.length === 7 ? color + alpha : color;
}

/**
 * The stacked Verlauf card: one Mira figure over time as a stacked bar per period, split into labelled
 * coloured parts that sum to the bar's total. It serves two jobs from one view model
 * ({@link MiraStackedTrendView}):
 *
 * - a **segment figure** (Projektablauf, Leistungen): pass `trends` + `segments` (the parts, each with
 *   its themed colour); the card reads each point's own breakdown and draws them **stacked** into one bar.
 * - a **comparison** across members or branches: pass `entities` (presence selects this mode) +
 *   `referenceTrends`; the card draws up to eight of them as **grouped** bars side by side per period (a
 *   double bar graph), and more than eight as the five levels across them (Best, 25 %, 50 %, 75 %, Worst;
 *   #2610112). A **total** draws a sum per bar, each with its share of the period's total in the tooltip;
 *   an **average / share** draws the mean / share per bar, hides any bar under the case floor with a note,
 *   carries each bar's case count in its tooltip, and offers Quartal / Jahr only. The metric decides
 *   which: {@link isAverageComparison}.
 *
 * It shares the Monat / Quartal / Jahr switch, the chosen-period shading and the value-axis unit with the
 * line card ({@link MiraTrendCardComponent}) through {@link MiraTrendChart}, so the two read the same. In
 * comparison mode it defaults to Quartal (five bars across 24 months is too dense). Hovering a bar dims
 * the others. All the maths is in the pure, unit-tested view model; this only draws it.
 */
@Component({
  components: {
    "bar-chart": BarChart,
    "mira-facts": MiraFactsComponent,
  },
})
export default class MiraStackedTrendCardComponent extends Vue {
  @Prop({ required: true })
  metricId!: string;

  @Prop({ required: true })
  title!: string;

  @Prop({ required: true })
  context!: MiraViewContext;

  @Prop({ required: true })
  formatValue!: MiraValueFormatter;

  @Prop({ default: null })
  selectedRange!: MiraTrendRange | null;

  /** A fixed caption shown in place of the range note (e.g. Projektablauf's cohort line). */
  @Prop({ default: null })
  caption!: string | null;

  /** Extra facts under the chart (e.g. Projektablauf's insurance split), reusing the facts list. */
  @Prop({ default: () => [] })
  facts!: MiraFact[];

  /** The chosen period's value per part key, overlaid in the legend ("(12)"); `null` for none. */
  @Prop({ default: null })
  legendValues!: Record<string, string> | null;

  /**
   * Comparison only: the reference's figure for the chosen period under the title ("Klünder: 5.638"), where
   * the ranking card that carried it is not shown (#2610112 X1). `null` for none.
   */
  @Prop({ default: null })
  headline!: string | null;

  // --- Segment mode (Projektablauf, Leistungen) ---
  @Prop({ default: null })
  trends!: Record<string, MiraTrendSeries> | undefined;

  @Prop({ default: () => [] })
  segments!: MiraStackedSegmentDef[];

  // --- Comparison mode (a total split across members / branches) ---
  /** Presence selects comparison mode: one part per entity, the five largest then "Weitere". */
  @Prop({ default: null })
  entities!: MiraStackEntity[] | null;

  @Prop({ default: null })
  referenceTrends!: Record<string, MiraTrendSeries> | undefined;

  /**
   * The stable colour per entity id, built once by the screen from the entities' size. A comparison bar
   * reads its colour from here, so a company / branch keeps its colour across every tile. Empty for a
   * segment figure (it colours its own parts).
   */
  @Prop({ default: () => ({}) })
  entityColors!: Record<string, string>;

  /**
   * Draw the entities in the order given rather than ranked (the Markt's own first). `type: Boolean` is
   * load-bearing: the screens pass the bare attribute, which without it arrives as "" and reads as false,
   * so the Markt was ranked by cases and drawn before the company (Taimoor, 2026-10-29).
   */
  @Prop({ type: Boolean, default: false })
  keepEntityOrder!: boolean;

  @Ref() barChartRef!: ExtractComponentData<typeof BarChart>;

  /** The dataset under the pointer, so the others dim; `null` when nothing is hovered. */
  hoveredIndex: number | null = null;

  get isComparison(): boolean {
    return this.entities !== null;
  }

  /**
   * A comparison of an average / share (not a total): grouped bars of the mean / share per entity, hidden
   * below the case floor, Quartal and Jahr only. Derived from the metric, so no extra prop is needed: in
   * comparison mode a figure is either a total (sum bars) or an average / share.
   */
  get isAverageComparison(): boolean {
    return this.isComparison && !isTotalMetric(this.metricId);
  }

  /** The grains the Monat / Quartal / Jahr switch offers: Quartal / Jahr only in a comparison. */
  get grains(): MiraTrendGrain[] {
    return this.isComparison ? MIRA_COMPARISON_GRAINS : MIRA_TREND_GRAINS;
  }

  /**
   * The grain shown: the one choice shared by every Mira timeline card (`MiraTrendGrainModule`), or this
   * card's first offered grain where it does not offer that choice (a comparison shows Quartal for Monat).
   */
  get grain(): MiraTrendGrain {
    return offeredGrain(MiraTrendGrainModule.grain, this.grains);
  }

  set grain(grain: MiraTrendGrain) {
    MiraTrendGrainModule.setGrain(grain);
  }

  /** The trends the grain-for-range switch reads: the reference's own in comparison mode. */
  get trendsForGrain(): Record<string, MiraTrendSeries> | undefined {
    return this.isComparison ? this.referenceTrends : this.trends;
  }

  @Watch("selectedRange", { immediate: true })
  onSelectedRange() {
    // A comparison shows Quartal for a chosen Monat without writing it back (see `grain`), so only a
    // period the shown grain has no point in (2023, before the trend's quarters) moves the shared choice.
    const grain = grainForRange(this.trendsForGrain ?? undefined, this.metricId, this.grain, this.selectedRange, this.context);
    if (grain !== this.grain) {
      this.grain = grain;
    }
  }

  get view(): MiraStackedTrendView | null {
    if (this.isComparison) {
      const entities = this.entities ?? [];
      return this.isAverageComparison
        ? buildAverageComparisonBars(this.metricId, this.grain, this.referenceTrends, entities, this.formatValue, this.context, this.entityColors, this.keepEntityOrder)
        : buildComparisonBars(this.metricId, this.grain, this.referenceTrends, entities, this.formatValue, this.context, this.entityColors, this.keepEntityOrder);
    }
    return buildStackedTrend(this.trends, this.metricId, this.grain, this.segments, this.formatValue, this.context);
  }

  /** Comparison only: how many entities rank below the five shown, for the caption under the chart. */
  get restNote(): string | null {
    const rest = this.view?.restCount ?? 0;
    return this.isComparison && rest > 0 ? this.$t("mira.stacked.restNote", [this.context.formatNumber(rest)]).toString() : null;
  }

  /** Whether the bars are the five levels across the entities rather than one bar per entity. */
  get isLevels(): boolean {
    return this.view?.levels === true;
  }

  /**
   * The line under a levels chart that says what the five bars are, and which period the names in the
   * legend belong to (#2610137).
   */
  get levelsNote(): string | null {
    if (!this.isLevels) {
      return null;
    }
    const note = this.$t("mira.levels.note").toString();
    const index = this.levelNamesIndex;
    return index === null ? note : `${note} ${this.$t("mira.levels.namesFor", [this.points[index].label])}`;
  }

  /**
   * The point whose clients the levels legend names (Hans, #2610137: "show the clients for each bar"): the
   * latest point of the chosen period with a bar, else the latest point with one. `null` off the levels.
   */
  get levelNamesIndex(): number | null {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const series = this.series;
    const span = this.periodSpan;
    if (!this.isLevels) {
      return null;
    }
    const hasBar = (index: number) => series.some((part) => part.values[index] !== null && part.values[index] !== undefined);
    const indexes = this.points.map((_point, index) => index).filter(hasBar);
    const inPeriod = span ? indexes.filter((index) => index >= span.first && index <= span.last) : [];
    const chosen = inPeriod.length > 0 ? inPeriod : indexes;
    return chosen.length > 0 ? chosen[chosen.length - 1] : null;
  }

  get hasData(): boolean {
    return this.view?.hasData ?? false;
  }

  get points() {
    return this.view?.points ?? [];
  }

  get series() {
    return this.view?.series ?? [];
  }

  get legendItems() {
    const values = this.legendValues;
    const namesAt = this.levelNamesIndex;
    return this.series.map((part) => ({
      key: part.key,
      label: part.label,
      color: part.color,
      // A level names its client at the chosen point (#2610137); otherwise the screen's figure, if any.
      valueText: (values ? values[part.key] : null) ?? (namesAt !== null ? part.entityLabels?.[namesAt] ?? null : null),
    }));
  }

  /** The points inside the chosen period, for the shading. */
  get periodSpan(): { first: number; last: number } | null {
    const range = this.selectedRange;
    if (!range) {
      return null;
    }
    const inside = this.points
      .map((point, index) => (point.from >= range.from && point.toExclusive <= range.toExclusive ? index : -1))
      .filter((index) => index >= 0);
    return inside.length ? { first: inside[0], last: inside[inside.length - 1] } : null;
  }

  get rangeNote(): string | null {
    const points = this.points;
    if (points.length === 0) {
      return null;
    }
    return this.$t("mira.trend.rangeNote", [points[0].label, points[points.length - 1].label]).toString();
  }

  get surfaceColor(): string {
    return themed(COLOR_STANDARD_WHITE, COLOR_SURFACE_DARK);
  }

  get chartStyles(): Record<string, string> {
    return { position: "relative", height: "100%" };
  }

  get notes(): string[] {
    const notes: string[] = [];
    const points = this.points;
    // Average / share comparison: some bars rested on fewer than the floor and were left out.
    if (this.isAverageComparison && this.view?.belowBarFloor) {
      notes.push(this.$t("mira.stacked.barFloorNote", [this.context.formatNumber(MIRA_COMPARISON_BAR_FLOOR)]).toString());
    }
    if (points.some((point) => point.belowFloor)) notes.push(this.$t("mira.trend.gapNote").toString());
    if (points.some((point) => point.partial)) notes.push(this.$t("mira.trend.partialNote").toString());
    if (this.periodSpan) notes.push(this.$t("mira.trend.periodNote").toString());
    return notes;
  }

  /** The fill a part takes at one point: lighter for a partial bar, dimmed when another part is hovered. */
  private fillFor(color: string, dsIndex: number, pointIndex: number): string {
    const point = this.points[pointIndex];
    if (this.hoveredIndex !== null && this.hoveredIndex !== dsIndex) {
      return withAlpha(color, DIM_ALPHA);
    }
    if (point?.partial) {
      return withAlpha(color, PARTIAL_ALPHA);
    }
    return color;
  }

  /** Re-creates the chart when its axis labels change (see {@link chartAxisKey}). */
  get chartKey(): string {
    return chartAxisKey(this.points.map((point) => point.label));
  }

  get chartData() {
    const points = this.points;
    const labels = points.map((point) => point.label);
    const surface = this.surfaceColor;
    const grouped = this.isComparison;
    const datasets = this.series.map((part, dsIndex) => {
      const base = {
        label: part.label,
        data: part.values,
        backgroundColor: points.map((_, pointIndex) => this.fillFor(part.color, dsIndex, pointIndex)),
      };
      // Grouped (comparison): bars sit side by side per period, with a small gap between neighbours and
      // no border. Stacked (a segment figure): a 2px surface border reads as the gap between the parts.
      return grouped
        ? { ...base, categoryPercentage: 0.8, barPercentage: 0.9 }
        : { ...base, borderColor: surface, borderWidth: 2, borderSkipped: false, stack: "stack" };
    });
    return { labels, datasets };
  }

  get chartOptions() {
    const points = this.points;
    const series = this.series;
    const formatValue = this.formatValue;
    const averageComparison = this.isAverageComparison;
    const t = (key: string, params: string[]) => this.context.t(key, params);
    const metricId = this.metricId;
    // A named total's bars are parts of the reference: each names its share of the period's total (X1).
    const levels = this.isLevels;
    const withShare = this.isComparison && !levels && isTotalMetric(metricId);
    const context = this.context;
    const periodColor = themed(COLOR_MIRA_TREND_PERIOD, COLOR_MIRA_TREND_PERIOD_DARK);
    const span = this.periodSpan;

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: MIRA_TREND_ANIMATION,
      interaction: { mode: "index", intersect: false },
      onHover: (event: unknown, _active: unknown, chart: {
        getElementsAtEventForMode: (e: unknown, m: string, o: unknown, u: boolean) => Array<{ datasetIndex: number }>;
      }) => {
        const hit = chart.getElementsAtEventForMode(event, "nearest", { intersect: true }, false);
        const next = hit.length ? hit[0].datasetIndex : null;
        if (next !== this.hoveredIndex) {
          this.hoveredIndex = next;
        }
      },
      scales: {
        // Grouped comparison bars are not stacked; a segment figure stacks on both axes.
        x: { offset: true, stacked: !this.isComparison },
        y: { beginAtZero: true, stacked: !this.isComparison, ...trendValueAxis(formatValue) },
      },
      plugins: {
        legend: { display: false },
        annotation: { annotations: trendPeriodAnnotation(span, periodColor) },
        tooltip: {
          callbacks: {
            title: (items: Array<{ dataIndex: number }>) => (items.length ? points[items[0].dataIndex]?.label ?? "" : ""),
            // One line per non-empty part, then the bar's total as the footer. A comparison bar appends its
            // case count in the figure's unit ("· 412 Projekte"); an average keeps a real 0 (a 0 % share).
            label: (item: { datasetIndex: number; dataIndex: number }) => {
              const part = series[item.datasetIndex];
              const value = part?.values[item.dataIndex];
              if (value === null || value === undefined) {
                return "";
              }
              // A level of 0 is a real mark (the lowest branch had nothing), so only a named bar's 0 is skipped.
              if (!averageComparison && !levels && value === 0) {
                return "";
              }
              const periodTotal = points[item.dataIndex]?.total ?? null;
              const share = withShare && periodTotal !== null && periodTotal > 0 ? `${formatPercent(value / periodTotal, context)} · ` : "";
              const entity = part?.entityLabels?.[item.dataIndex];
              const line = `${part?.label}: ${share}${formatValue(value)}${entity ? ` (${entity})` : ""}`;
              const cases = part?.basis?.[item.dataIndex];
              if (cases !== null && cases !== undefined) {
                return `${line} · ${casesText(metricId, cases, context)}`;
              }
              return line;
            },
            footer: (items: Array<{ dataIndex: number }>) => {
              const point = items.length ? points[items[0].dataIndex] : null;
              return point && point.total !== null ? t("mira.stacked.tooltipTotal", [point.totalText ?? ""]) : "";
            },
          },
        },
      },
    };
  }

  /**
   * The pointer left the chart: clear the hover dim and, explicitly, the chart's own tooltip and active
   * bars. The tooltip could otherwise stay on screen wherever the pointer went (Taimoor, 2026-10-07): the
   * hover dim redraws the chart on every hover, a redraw replays the last pointer position, and a missed
   * "pointer left" event then brings the tooltip back with nothing left to clear it. Clearing it through
   * Chart.js also tells the tooltip to ignore replays until the pointer returns.
   */
  onLeave() {
    this.hoveredIndex = null;
    const chart = this.barChartRef?.chartInstance;
    if (!chart) {
      return;
    }
    chart.setActiveElements([]);
    chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
    chart.update("none");
  }
}
